import React, { useState } from "react";

import { Input, InputSelect, TextArea } from "../../../../form";
import ToggleComponent from "../../../../common/toggleComponent";
import { ImageUploader, ColorPicker } from "../../../../common";
import { uploadFileIfNeeded, convertRGBAObjectToString } from "../../../../../utils";

import { MinusSquare, SortIcon } from "../../../../../asssets/icons";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/24/outline";

const componentTitles = {
  classPage: {
    studentsList: "Students List",
    strengthWordCloud: "Strength Wordcloud",
    professionRolesImageCloud: "Professional Roles Image Cloud",
    profileVideoSelection: "Profile Video Selection",
    professionSelection: "Profession Selection",
    downloadAllProfessions: "Download All Students Professions",
    aptitudeWordCloud: "Aptitude Wordcloud",
    regionalLinks: "Regional Links",
    htmlBox: "HTML Box",
    videosPlayer: "Videos Player",
    classCommunicationPreferences: "Class Communication Preferences",
    classMapSelections: "Class Map Selections",
    teamProfilePage: "Team Profile Webpage",
    dataTable: "Data Table",
    studentRandomInputsWordCloud: "Student Random Inputs Word Cloud",
    infoPopup: "Info Popup"
  },
  studentPage: {
    preferredStrengths: "3 Preferred Strengths",
    preferredProfessionalRoles: "3 Preferred Professional Roles",
    similarProfileBadges: "Similar Profile Badges",
    professionSelection: "Profession Selection",
    profileDownload: "Profile Download",
    profileLinkCopy: "Profile Link Copy",
    htmlBox: "HTML Box",
    videosPlayer: "Videos Player",
    studentCommunicationPreferences: "Student Communication Preferences",
    studentRandomInputs: "Student Random Inputs",
    infoPopup: "Info Popup"
  },
  parentPage: {
    preferredStrengths: "3 Preferred Strengths",
    preferredProfessionalRoles: "3 Preferred Professional Roles",
    similarProfileBadges: "Similar Profile Badges",
    professionSelection: "Profession Selection",
    profileDownload: "Profile Download",
    profileLinkCopy: "Profile Link Copy",
    htmlBox: "HTML Box",
    videosPlayer: "Videos Player",
    studentCommunicationPreferences: "Student Communication Preferences",
    studentRandomInputs: "Student Random Inputs",
    infoPopup: "Info Popup"
  },
  teamProfilePage: {
    studentsList: "Students List",
    strengthWordCloud: "Strength Wordcloud",
    professionRolesImageCloud: "Professional Roles Image Cloud",
    profileVideoSelection: "Profile Video Selection",
    professionSelection: "Profession Selection",
    downloadAllProfessions: "Download All Students Professions",
    aptitudeWordCloud: "Aptitude Wordcloud",
    regionalLinks: "Regional Links",
    htmlBox: "HTML Box",
    videosPlayer: "Videos Player",
    classCommunicationPreferences: "Class Communication Preferences",
    teamOrganizations: "Team Organizations",
    singleQuestionBox: "Single Question Text Box",
    dataTable: "Data Table",
    attitudesComponent: "Attitudes Component",
    infoPopup: "Info Popup"
  }
};

const downloadTypeOptions = [
  { value: "default", label: "Default" },
  { value: "strengthsRolesPdf", label: "Strengths & Roles PDF" }
];

const ClassStudentComponents = ({
  index,
  region,
  externalProjectType,
  type = "classPage",
  projectVariant,
  projectVariantTitle,
  componentType,
  backgroundHeading,
  updateBackgroundHeading,
  foregroundHeading,
  updateForegroundHeading,
  subText,
  updateSubText,
  buttonText,
  updateButtonText,
  downloadType,
  updateDownloadType,
  html,
  updateHTMLBox,
  videos,
  updateVideosPlayerTitle,
  updateVideosPlayerUrl,
  addNewVideoInVideosPlayer,
  deleteVideoInVideosPlayer,
  videPlayerAptitudeCloudEnabled,
  toggleVideoPlayerAptitudeCloud,
  regions,
  selectedRegionalLinks,
  selectProjectTypeRegionalLink,
  availableUsersGroups = [],
  selectedUsersGroups = [],
  selectUsersGroup,
  totalBadgesToShow,
  updateTotalBadgesToShow,
  discoverChampionCustomUrl,
  discoverChampionCustomUrlValue,
  professionTypes,
  selectedProfessionType,
  selectProfessionType,
  useOrganizationStrengthsListInAptitudeCloud,
  toggleUseOrganizationStrengthsListInAptitudeCloud,
  useOrganizationRolesListInAptitudeCloud,
  toggleUseOrganizationRolesListInAptitudeCloud,
  useOrganizationProfessionsListInAptitudeCloud,
  toggleUseOrganizationProfessionsListInAptitudeCloud,
  showAsList,
  toggleShowAsListInAptitudeCloud,
  aptitudeCloudOptions = [],
  updateAptitudeCloudOption,
  addAptitudeCloudOption,
  deleteAptitudeCloudOption,
  customVideoOptions,
  addCustomVideoOption,
  deleteCustomVideoOption,
  updateCustomVideoOption,
  columns = [],
  updateTableColumn,
  addTableColumn,
  deleteTableColumn,
  attitudesList = [],
  addAttitudeListItem,
  updateAttitudeListItem,
  deleteAttitudeListItem,
  updateAttitudeComponentField,
  attitudesTitle,
  attitudesSubtitle,
  attitudesTitleNumber2,
  attitudesSubtitleNumber2,
  attitudesSubmitButtonText,
  updateMapImage,
  mapImages,
  removeComponent,
  hideInPdf,
  toggleShowClassStudentComponentInPdf,
  listLength,
  updateListLength,
  replacePreviousSelections,
  toggleReplacePreviousSelections,
  studentAttributesToMatch = [],
  selectStudentAttributesToMatch,
  defaultStudentAttributesToMatch = [],
  regionalEvents = [],
  selectedRegionalEvents = [],
  selectedRegionalEventProfileTypeId,
  setRegionalEventProfileType,
  selectRegionalEvent,
  similarProfileBadgesNavigationPopup = {},
  updateSimilarProfileBadgesNavigationPopupField,
  subInputField,
  updateSubInputField,
  updateStudentRandomInputsNumber,
  studentRandomInputsNumber,
  studentRandomInputsPopupHeading,
  studentRandomInputsPopupSubmitButtonText,
  studentRandomInputsPopupInputPlaceholder,
  studentRandomInputsWordCloudId,
  updateStudentRandomInputsField,
  studentRandomInputsOptions = [],
  addStudentRandomInputsOption,
  removeStudentRandomInputsOption,
  updateStudentRandomInputsOption,
  infoPopupHeading,
  infoPopupSubHeading,
  infoPopupHeadingColor,
  infoPopupSubHeadingColor,
  infoPopupBackgroundColor,
  infoPopupCloseIconBackgroundColor,
  infoPopupCloseIconColor,
  infoPopupButtonBackgroundColor,
  infoPopupButtonTextColor,
  infoPopupAutoShow,
  updateInfoPopupField
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [imageUploading, setImageUploading] = useState({});

  const handleImageUpload = async (keyPath, image) => {
    try {
      const imageUrl = await uploadFileIfNeeded(image, "classMapSelections", (progress) => {
        setImageUploading({ ...imageUploading, [keyPath]: progress?.progress });
      });
      setImageUploading({ ...imageUploading, [keyPath]: 0 });

      return imageUrl;
    } catch (error) {
      console.log(error);
      setImageUploading({ ...imageUploading, [keyPath]: 0 });
    }
  };

  return (
    <div className="p-3 rounded-md bg-gray-200 w-full relative">
      <span className="block w-4 h-4 absolute left-3 top-3 cursor-grab">
        <SortIcon />
      </span>
      <span className="block w-5 h-5 absolute right-3 top-3 cursor-pointer text-red-600">
        <TrashIcon
          onClick={() => removeComponent(type, projectVariant, index, region, externalProjectType)}
        />
      </span>
      {toggleShowClassStudentComponentInPdf && (
        <div className="flex items-center justify-between my-5">
          <p className="font-bold">Show in PDF</p>
          <div className="flex gap-3 items-center">
            <ToggleComponent
              handleCompToggle={() =>
                toggleShowClassStudentComponentInPdf(
                  type,
                  projectVariant,
                  index,
                  region,
                  externalProjectType
                )
              }
              isToggle={!hideInPdf}
            />
          </div>
        </div>
      )}

      <p
        className="font-bold text-base text-center mt-2 mb-4 cursor-pointer"
        onClick={() => setCollapsed(!collapsed)}>
        <span className="inline-block px-2 py-1 rounded text-center bg-corange-100 text-black mb-2">
          {type === "classPage"
            ? "Class Page"
            : type === "teamProfilePage"
              ? "Team Profile Page"
              : type === "parentPage"
                ? "Parent Page"
                : "Student Page"}{" "}
          - {projectVariantTitle} - comp# {index + 1}
        </span>
        <br />
        {componentTitles[type]?.[componentType]}
      </p>

      {!collapsed && (
        <>
          {componentType !== "htmlBox" && componentType !== "infoPopup" && (
            <>
              {updateForegroundHeading !== undefined && (
                <>
                  <p className="font-bold">Forground Heading</p>
                  <div className="flex flex-row gap-1 my-2">
                    {["en", "de", "fr", "it"].map((lang) => {
                      return (
                        <Input
                          key={`foregroundHeading-${lang}`}
                          className={"w-full"}
                          inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                          labelClassName={"text-sm font-normal text-black"}
                          label={lang.toUpperCase() + " Title"}
                          placeholder={lang}
                          value={foregroundHeading?.[lang]}
                          onChange={(e) => {
                            if (updateForegroundHeading)
                              updateForegroundHeading(
                                type,
                                projectVariant,
                                index,
                                lang,
                                e.target.value,
                                region,
                                externalProjectType
                              );
                          }}
                        />
                      );
                    })}
                  </div>
                </>
              )}

              {updateBackgroundHeading !== undefined && (
                <>
                  <p className="font-bold">Background Heading</p>
                  <div className="flex flex-row gap-1 my-2">
                    {["en", "de", "fr", "it"].map((lang) => {
                      return (
                        <Input
                          key={`backgroundHeading-${lang}`}
                          className={"w-full"}
                          inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                          labelClassName={"text-sm font-normal text-black"}
                          label={lang.toUpperCase() + " Title"}
                          placeholder={lang}
                          value={backgroundHeading?.[lang]}
                          onChange={(e) => {
                            if (updateForegroundHeading)
                              updateBackgroundHeading(
                                type,
                                projectVariant,
                                index,
                                lang,
                                e.target.value,
                                region,
                                externalProjectType
                              );
                          }}
                        />
                      );
                    })}
                  </div>
                </>
              )}

              {updateSubText !== undefined && (
                <>
                  <p className="font-bold">Sub Text</p>
                  <div className="flex flex-row gap-1 my-2">
                    {["en", "de", "fr", "it"].map((lang) => {
                      return (
                        <Input
                          key={`subText-${lang}`}
                          className={"w-full"}
                          inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                          labelClassName={"text-sm font-normal text-black"}
                          label={lang.toUpperCase() + " Title"}
                          placeholder={lang}
                          value={subText?.[lang]}
                          onChange={(e) => {
                            if (updateSubText)
                              updateSubText(
                                type,
                                projectVariant,
                                index,
                                lang,
                                e.target.value,
                                region,
                                externalProjectType
                              );
                          }}
                        />
                      );
                    })}
                  </div>
                </>
              )}

              {updateButtonText !== undefined && (
                <>
                  <p className="font-bold">Button Text</p>
                  <div className="flex flex-row gap-1 my-2">
                    {["en", "de", "fr", "it"].map((lang) => {
                      return (
                        <Input
                          key={`buttonText-${lang}`}
                          className={"w-full"}
                          inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                          labelClassName={"text-sm font-normal text-black"}
                          label={lang.toUpperCase() + " Title"}
                          placeholder={lang}
                          value={buttonText?.[lang]}
                          onChange={(e) => {
                            if (updateButtonText)
                              updateButtonText(
                                type,
                                projectVariant,
                                index,
                                lang,
                                e.target.value,
                                region,
                                externalProjectType
                              );
                          }}
                        />
                      );
                    })}
                  </div>
                </>
              )}

              {componentType === "profileDownload" && (
                <div className="w-full mt-2">
                  <p className="font-bold">Download Type</p>
                  <InputSelect
                    name={projectVariant + "-" + componentType + "-" + index + "-downloadType"}
                    className={"mb-0"}
                    placeholder="Select download type..."
                    options={downloadTypeOptions}
                    value={
                      downloadType
                        ? downloadTypeOptions.find((o) => o.value === downloadType) || null
                        : null
                    }
                    onChange={(e) =>
                      updateDownloadType(
                        type,
                        projectVariant,
                        index,
                        e.value,
                        region,
                        externalProjectType
                      )
                    }
                  />
                </div>
              )}

              {componentType === "regionalLinks" && (
                <div className="w-full mt-2">
                  <p className="font-bold">Selected Regional Links</p>
                  <InputSelect
                    name={projectVariant + "-" + componentType + "-" + index}
                    className={"mb-0"}
                    options={regions
                      .map((region) => {
                        if (selectedRegionalLinks.includes(region._id)) return null;
                        return {
                          value: region._id,
                          label:
                            region.name_en || region.name_de || region.name_fr || region.name_it
                        };
                      })
                      .filter((value) => value !== null)}
                    onChange={(e) =>
                      selectProjectTypeRegionalLink(
                        type,
                        projectVariant,
                        index,
                        e.value,
                        region,
                        externalProjectType
                      )
                    }
                  />

                  {selectedRegionalLinks.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedRegionalLinks.map((regionId) => {
                        const region = regions.find((region) => region._id === regionId);
                        return (
                          <span
                            key={regionId}
                            className="px-2 py-1 rounded-full flex gap-3 items-center">
                            {region?.name_en || region?.name_de || region?.name_fr}{" "}
                            <button
                              onClick={() =>
                                selectProjectTypeRegionalLink(
                                  type,
                                  projectVariant,
                                  index,
                                  regionId,
                                  region,
                                  externalProjectType
                                )
                              }>
                              <MinusSquare />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {["profileVideoSelection", "professionSelection", "similarProfileBadges"].includes(
                componentType
              ) && (
                <div className="w-full mt-2">
                  <p className="font-bold mt-4">Total badges to show</p>
                  <Input
                    type="number"
                    min={0}
                    className={"w-full"}
                    inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                    labelClassName={"text-sm font-normal text-black"}
                    // label={"Custom URL"}
                    placeholder={"Total badges to show"}
                    value={totalBadgesToShow || 0}
                    onChange={(e) => {
                      if (updateTotalBadgesToShow)
                        updateTotalBadgesToShow(
                          type,
                          projectVariant,
                          index,
                          e.target.value,
                          region,
                          externalProjectType
                        );
                    }}
                  />

                  <p className="font-bold mt-4">Discover Champions Custom URL</p>
                  <Input
                    className={"w-full"}
                    inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                    labelClassName={"text-sm font-normal text-black"}
                    // label={"Custom URL"}
                    placeholder={"Custom URL"}
                    value={discoverChampionCustomUrlValue}
                    onChange={(e) => {
                      if (discoverChampionCustomUrl)
                        discoverChampionCustomUrl(
                          type,
                          projectVariant,
                          index,
                          e.target.value,
                          region,
                          externalProjectType
                        );
                    }}
                  />

                  <p className="font-bold mt-2">Users Groups</p>
                  <InputSelect
                    name={projectVariant + "-" + componentType + "-" + index}
                    className={"mb-0"}
                    options={availableUsersGroups
                      .map((userGroup) => {
                        return {
                          value: userGroup._id,
                          label: `${userGroup?.groupColumn?.name} - ${userGroup.name}`
                        };
                      })
                      .filter(
                        (userGroup) =>
                          !selectedUsersGroups.includes(userGroup.value) && userGroup !== null
                      )}
                    onChange={(e) =>
                      selectUsersGroup(
                        type,
                        projectVariant,
                        index,
                        e.value,
                        region,
                        externalProjectType
                      )
                    }
                  />

                  {selectedUsersGroups?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedUsersGroups.map((userGroup) => {
                        const selectedGroupItem = availableUsersGroups?.find(
                          (g) => g?._id === userGroup
                        );

                        if (!selectedGroupItem) return null;

                        return (
                          <span
                            key={userGroup}
                            className="px-2 py-1 rounded-full flex gap-3 items-center">
                            [
                            <span className="font-bold">
                              {selectedGroupItem?.groupColumn?.name} -
                            </span>
                            <span className="text-cyan-800 font-semibold">
                              {selectedGroupItem?.name}
                            </span>
                            ]
                            <button
                              onClick={() =>
                                selectUsersGroup(
                                  type,
                                  projectVariant,
                                  index,
                                  userGroup,
                                  region,
                                  externalProjectType
                                )
                              }>
                              <MinusSquare />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {componentType === "similarProfileBadges" && (
                    <>
                      <p className="font-bold mt-4">Student Attributes to Match</p>
                      <InputSelect
                        name={
                          projectVariant + "-" + componentType + "-" + index + "-studentAttributes"
                        }
                        className={"mb-0"}
                        options={defaultStudentAttributesToMatch
                          .map((attribute) => {
                            return {
                              value: attribute.id,
                              label: attribute.value
                            };
                          })
                          .filter(
                            (attribute) =>
                              !studentAttributesToMatch.includes(attribute.value) &&
                              attribute !== null
                          )}
                        onChange={(e) =>
                          selectStudentAttributesToMatch(
                            type,
                            projectVariant,
                            index,
                            e.value,
                            region,
                            externalProjectType
                          )
                        }
                      />

                      {studentAttributesToMatch?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {studentAttributesToMatch.map((attributeId) => {
                            const selectedAttribute = defaultStudentAttributesToMatch?.find(
                              (attr) => attr?.id === attributeId
                            );

                            if (!selectedAttribute) return null;

                            return (
                              <span
                                key={attributeId}
                                className="px-2 py-1 rounded-full flex gap-3 items-center bg-blue-100">
                                <span className="text-blue-800 font-semibold">
                                  {selectedAttribute?.value}
                                </span>
                                <button
                                  onClick={() =>
                                    selectStudentAttributesToMatch(
                                      type,
                                      projectVariant,
                                      index,
                                      attributeId,
                                      region,
                                      externalProjectType
                                    )
                                  }>
                                  <MinusSquare />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}

                  {componentType === "similarProfileBadges" && (
                    <>
                      <p className="font-bold mt-4">Regional Events Profile Type</p>
                      <InputSelect
                        name={projectVariant + "-" + componentType + "-" + index + "-profileType"}
                        className={"mb-0"}
                        placeholder="Select profile type..."
                        options={(() => {
                          const profileTypeOptions = [];
                          regionalEvents?.forEach((event) => {
                            if (
                              event?.profileTypeId &&
                              !profileTypeOptions.some(
                                (option) => option.value === event?.profileTypeId?._id
                              )
                            ) {
                              profileTypeOptions.push({
                                value: event?.profileTypeId?._id,
                                label: event?.profileTypeId?.name
                              });
                            }
                          });
                          return profileTypeOptions;
                        })()}
                        value={
                          selectedRegionalEventProfileTypeId
                            ? {
                                value: selectedRegionalEventProfileTypeId,
                                label:
                                  regionalEvents?.find(
                                    (e) =>
                                      e?.profileTypeId?._id === selectedRegionalEventProfileTypeId
                                  )?.profileTypeId?.name || "Selected Profile Type"
                              }
                            : null
                        }
                        onChange={(e) =>
                          setRegionalEventProfileType(
                            type,
                            projectVariant,
                            index,
                            e.value,
                            region,
                            externalProjectType
                          )
                        }
                      />

                      {selectedRegionalEventProfileTypeId && (
                        <>
                          <p className="font-bold mt-4">Regional Events</p>
                          <InputSelect
                            name={
                              projectVariant + "-" + componentType + "-" + index + "-regionalEvents"
                            }
                            className={"mb-0"}
                            placeholder="Select regional events..."
                            options={regionalEvents
                              ?.filter(
                                (event) =>
                                  event?.profileTypeId?._id ===
                                    selectedRegionalEventProfileTypeId &&
                                  !selectedRegionalEvents.includes(event._id)
                              )
                              ?.map((event) => ({
                                value: event._id,
                                label:
                                  event.name_en ||
                                  event.name_de ||
                                  event.name_fr ||
                                  event.name_it ||
                                  "Unnamed Event"
                              }))}
                            onChange={(e) =>
                              selectRegionalEvent(
                                type,
                                projectVariant,
                                index,
                                e.value,
                                region,
                                externalProjectType
                              )
                            }
                          />

                          {selectedRegionalEvents?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {selectedRegionalEvents.map((eventId) => {
                                const selectedEvent = regionalEvents?.find(
                                  (event) => event?._id === eventId
                                );

                                if (!selectedEvent) return null;

                                return (
                                  <span
                                    key={eventId}
                                    className="px-2 py-1 rounded-full flex gap-3 items-center bg-green-100">
                                    <span className="text-green-800 font-semibold">
                                      {selectedEvent?.name_en ||
                                        selectedEvent?.name_de ||
                                        selectedEvent?.name_fr ||
                                        selectedEvent?.name_it ||
                                        "Unnamed Event"}
                                    </span>
                                    <button
                                      onClick={() =>
                                        selectRegionalEvent(
                                          type,
                                          projectVariant,
                                          index,
                                          eventId,
                                          region,
                                          externalProjectType
                                        )
                                      }>
                                      <MinusSquare />
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {componentType === "similarProfileBadges" && (
                    <div className="border border-gray-300 p-3 rounded-md mt-4">
                      <div className="flex items-center justify-between my-2">
                        <p className="font-bold">Navigation Popup</p>
                        <ToggleComponent
                          handleCompToggle={() => {
                            if (updateSimilarProfileBadgesNavigationPopupField)
                              updateSimilarProfileBadgesNavigationPopupField(
                                type,
                                projectVariant,
                                index,
                                "enabled",
                                !similarProfileBadgesNavigationPopup?.enabled,
                                region,
                                externalProjectType
                              );
                          }}
                          isToggle={!!similarProfileBadgesNavigationPopup?.enabled}
                        />
                      </div>

                      {similarProfileBadgesNavigationPopup?.enabled && (
                        <>
                          <p className="font-bold mt-4">Header</p>
                          <div className="flex flex-row gap-1 my-2">
                            {["en", "de", "fr", "it"].map((lang) => (
                              <Input
                                key={`navigation-popup-header-${lang}`}
                                className={"w-full"}
                                inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                                labelClassName={"text-sm font-normal text-black"}
                                label={lang.toUpperCase() + " Header"}
                                placeholder={lang}
                                value={similarProfileBadgesNavigationPopup?.header?.[lang] || ""}
                                onChange={(e) => {
                                  if (updateSimilarProfileBadgesNavigationPopupField)
                                    updateSimilarProfileBadgesNavigationPopupField(
                                      type,
                                      projectVariant,
                                      index,
                                      "header",
                                      e.target.value,
                                      region,
                                      externalProjectType,
                                      lang
                                    );
                                }}
                              />
                            ))}
                          </div>

                          <p className="font-bold mt-4">Subtitle</p>
                          <div className="flex flex-row gap-1 my-2">
                            {["en", "de", "fr", "it"].map((lang) => (
                              <Input
                                key={`navigation-popup-subtitle-${lang}`}
                                className={"w-full"}
                                inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                                labelClassName={"text-sm font-normal text-black"}
                                label={lang.toUpperCase() + " Subtitle"}
                                placeholder={lang}
                                value={
                                  similarProfileBadgesNavigationPopup?.subtitle?.[lang] || ""
                                }
                                onChange={(e) => {
                                  if (updateSimilarProfileBadgesNavigationPopupField)
                                    updateSimilarProfileBadgesNavigationPopupField(
                                      type,
                                      projectVariant,
                                      index,
                                      "subtitle",
                                      e.target.value,
                                      region,
                                      externalProjectType,
                                      lang
                                    );
                                }}
                              />
                            ))}
                          </div>

                          <p className="font-bold mt-4">Confirm Button Text</p>
                          <div className="flex flex-row gap-1 my-2">
                            {["en", "de", "fr", "it"].map((lang) => (
                              <Input
                                key={`navigation-popup-confirm-${lang}`}
                                className={"w-full"}
                                inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                                labelClassName={"text-sm font-normal text-black"}
                                label={lang.toUpperCase() + " Confirm"}
                                placeholder={lang}
                                value={
                                  similarProfileBadgesNavigationPopup?.confirmButtonText?.[
                                    lang
                                  ] || ""
                                }
                                onChange={(e) => {
                                  if (updateSimilarProfileBadgesNavigationPopupField)
                                    updateSimilarProfileBadgesNavigationPopupField(
                                      type,
                                      projectVariant,
                                      index,
                                      "confirmButtonText",
                                      e.target.value,
                                      region,
                                      externalProjectType,
                                      lang
                                    );
                                }}
                              />
                            ))}
                          </div>

                          <p className="font-bold mt-4">Cancel Button Text</p>
                          <div className="flex flex-row gap-1 my-2">
                            {["en", "de", "fr", "it"].map((lang) => (
                              <Input
                                key={`navigation-popup-cancel-${lang}`}
                                className={"w-full"}
                                inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                                labelClassName={"text-sm font-normal text-black"}
                                label={lang.toUpperCase() + " Cancel"}
                                placeholder={lang}
                                value={
                                  similarProfileBadgesNavigationPopup?.cancelButtonText?.[lang] ||
                                  ""
                                }
                                onChange={(e) => {
                                  if (updateSimilarProfileBadgesNavigationPopupField)
                                    updateSimilarProfileBadgesNavigationPopupField(
                                      type,
                                      projectVariant,
                                      index,
                                      "cancelButtonText",
                                      e.target.value,
                                      region,
                                      externalProjectType,
                                      lang
                                    );
                                }}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {componentType === "professionSelection" && (
                <div className="w-full mt-2">
                  <p className="font-bold">
                    Selected Profession Type:{" "}
                    <span className="text-cyan-600">{selectedProfessionType}</span>
                  </p>
                  <InputSelect
                    name={projectVariant + "-" + componentType + "-" + index + "-professionType"}
                    className={"mb-0"}
                    options={["", ...professionTypes].map((professionType) => {
                      return {
                        value: professionType,
                        label: professionType
                      };
                    })}
                    onChange={(e) =>
                      selectProfessionType(
                        type,
                        projectVariant,
                        index,
                        e.value,
                        region,
                        externalProjectType
                      )
                    }
                  />
                </div>
              )}

              {componentType === "videosPlayer" && (
                <>
                  <div className="flex items-center justify-between my-5">
                    <p className="font-bold">Enable Aptitude Cloud</p>
                    <div className="flex gap-3 items-center">
                      <ToggleComponent
                        handleCompToggle={() =>
                          toggleVideoPlayerAptitudeCloud(
                            type,
                            projectVariant,
                            index,
                            region,
                            externalProjectType
                          )
                        }
                        isToggle={videPlayerAptitudeCloudEnabled}
                      />
                    </div>
                  </div>

                  <p className="font-bold mt-5">Videos</p>
                  <div className="flex flex-col gap-1 my-2">
                    {videos?.map((video, videoIndex) => {
                      return (
                        <div
                          key={videoIndex}
                          className="flex flex-row items-center justify-center gap-1 my-2">
                          {["en", "de", "fr", "it"].map((lang) => {
                            return (
                              <Input
                                className={"w-full"}
                                inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                                labelClassName={"text-sm font-normal text-black"}
                                label={lang.toUpperCase() + " Title"}
                                placeholder={lang}
                                value={video?.title?.[lang]}
                                onChange={(e) => {
                                  if (updateVideosPlayerTitle)
                                    updateVideosPlayerTitle(
                                      type,
                                      projectVariant,
                                      index,
                                      videoIndex,
                                      lang,
                                      e.target.value,
                                      region,
                                      externalProjectType
                                    );
                                }}
                              />
                            );
                          })}
                          <Input
                            className={"w-full"}
                            inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                            labelClassName={"text-sm font-normal text-black"}
                            label={"Video URL"}
                            placeholder={"Video URL"}
                            value={video?.url}
                            onChange={(e) => {
                              if (updateVideosPlayerUrl)
                                updateVideosPlayerUrl(
                                  type,
                                  projectVariant,
                                  index,
                                  videoIndex,
                                  e.target.value,
                                  region,
                                  externalProjectType
                                );
                            }}
                          />
                          <button
                            className="text-red-600 p-2"
                            onClick={() =>
                              deleteVideoInVideosPlayer(
                                type,
                                projectVariant,
                                index,
                                videoIndex,
                                region,
                                externalProjectType
                              )
                            }>
                            <TrashIcon className="w-7 h-7 mt-5" />
                          </button>
                        </div>
                      );
                    })}
                    <button
                      className="bg-green-600 text-white rounded-md p-2 flex gap-2 items-center justify-center"
                      onClick={() =>
                        addNewVideoInVideosPlayer(
                          type,
                          projectVariant,
                          index,
                          region,
                          externalProjectType
                        )
                      }>
                      <PlusCircleIcon className="w-7 h-7" /> Add New Video
                    </button>
                  </div>
                </>
              )}

              {componentType === "aptitudeWordCloud" && type !== "studentPage" && (
                <>
                  <p className="font-bold mt-5">Aptitude Cloud Options</p>
                  <div className="flex items-center justify-between">
                    <p className="font-bold">Disable to show as a list</p>
                    <div className="flex gap-3 items-center">
                      <ToggleComponent
                        handleCompToggle={() =>
                          toggleShowAsListInAptitudeCloud(
                            type,
                            projectVariant,
                            index,
                            region,
                            externalProjectType
                          )
                        }
                        isToggle={showAsList}
                      />
                      {showAsList && (
                        <Input
                          className={"w-20"}
                          inputClassName={"rounded-lg border-none bg-[#F6F5F5] py-2 px-2"}
                          value={listLength}
                          type="number"
                          min={1}
                          onChange={(e) => {
                            if (updateListLength)
                              updateListLength(
                                type,
                                projectVariant,
                                index,
                                e.target.value,
                                region,
                                externalProjectType
                              );
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="font-bold">Replace Previous Selections</p>
                    <div className="flex gap-3 items-center">
                      <ToggleComponent
                        handleCompToggle={() =>
                          toggleReplacePreviousSelections(
                            type,
                            projectVariant,
                            index,
                            region,
                            externalProjectType
                          )
                        }
                        isToggle={replacePreviousSelections}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="font-bold">Use organization Strengths List</p>
                    <div className="flex gap-3 items-center">
                      <ToggleComponent
                        handleCompToggle={() =>
                          toggleUseOrganizationStrengthsListInAptitudeCloud(
                            type,
                            projectVariant,
                            index,
                            region,
                            externalProjectType
                          )
                        }
                        isToggle={useOrganizationStrengthsListInAptitudeCloud}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="font-bold">Use organization Roles List</p>
                    <div className="flex gap-3 items-center">
                      <ToggleComponent
                        handleCompToggle={() =>
                          toggleUseOrganizationRolesListInAptitudeCloud(
                            type,
                            projectVariant,
                            index,
                            region,
                            externalProjectType
                          )
                        }
                        isToggle={useOrganizationRolesListInAptitudeCloud}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <p className="font-bold">Use organization Professions List</p>
                    <div className="flex gap-3 items-center">
                      <ToggleComponent
                        handleCompToggle={() =>
                          toggleUseOrganizationProfessionsListInAptitudeCloud(
                            type,
                            projectVariant,
                            index,
                            region,
                            externalProjectType
                          )
                        }
                        isToggle={useOrganizationProfessionsListInAptitudeCloud}
                      />
                    </div>
                  </div>

                  {useOrganizationProfessionsListInAptitudeCloud && (
                    <div className="w-full mb-2">
                      <p className="font-bold">
                        Selected Profession Type:{" "}
                        <span className="text-cyan-600">{selectedProfessionType}</span>
                      </p>
                      <InputSelect
                        name={
                          projectVariant + "-" + componentType + "-" + index + "-professionType"
                        }
                        className={"mb-0"}
                        options={["", ...professionTypes].map((professionType) => {
                          return {
                            value: professionType,
                            label: professionType
                          };
                        })}
                        onChange={(e) =>
                          selectProfessionType(
                            type,
                            projectVariant,
                            index,
                            e.value,
                            region,
                            externalProjectType
                          )
                        }
                      />
                    </div>
                  )}

                  {!useOrganizationStrengthsListInAptitudeCloud &&
                    !useOrganizationRolesListInAptitudeCloud &&
                    !useOrganizationProfessionsListInAptitudeCloud && (
                      <div className="flex flex-col gap-1 my-2">
                        {aptitudeCloudOptions?.map((option, aptitudeCloudOptionIndex) => {
                          return (
                            <div
                              key={`aptitiude-options-${index}-${aptitudeCloudOptionIndex}`}
                              className="flex flex-row items-center justify-center gap-1 my-2">
                              {["en", "de", "fr", "it"].map((lang) => {
                                return (
                                  <Input
                                    className={"w-full"}
                                    inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                                    labelClassName={"text-sm font-normal text-black"}
                                    label={lang.toUpperCase() + " Title"}
                                    placeholder={lang}
                                    value={option?.[lang]}
                                    onChange={(e) => {
                                      if (updateAptitudeCloudOption)
                                        updateAptitudeCloudOption(
                                          type,
                                          projectVariant,
                                          index,
                                          aptitudeCloudOptionIndex,
                                          lang,
                                          e.target.value,
                                          region,
                                          externalProjectType
                                        );
                                    }}
                                  />
                                );
                              })}

                              <button
                                className="text-red-600 p-2"
                                onClick={() => {
                                  if (deleteAptitudeCloudOption)
                                    deleteAptitudeCloudOption(
                                      type,
                                      projectVariant,
                                      index,
                                      aptitudeCloudOptionIndex,
                                      region,
                                      externalProjectType
                                    );
                                }}>
                                <TrashIcon className="w-7 h-7 mt-5" />
                              </button>
                            </div>
                          );
                        })}
                        <button
                          className="bg-green-600 text-white rounded-md p-2 flex gap-2 items-center justify-center"
                          onClick={() =>
                            addAptitudeCloudOption(
                              type,
                              projectVariant,
                              index,
                              region,
                              externalProjectType
                            )
                          }>
                          <PlusCircleIcon className="w-7 h-7" /> Add New Option
                        </button>
                      </div>
                    )}
                </>
              )}

              {componentType === "studentRandomInputs" && (
                <>
                  <Input
                    className={"w-full"}
                    inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                    value={studentRandomInputsNumber}
                    type="number"
                    min={1}
                    step={1}
                    placeholder="Number of Inputs"
                    label="Number of Inputs"
                    labelClassName="text-sm font-normal text-black"
                    onChange={(e) => {
                      if (updateStudentRandomInputsNumber)
                        updateStudentRandomInputsNumber(
                          type,
                          projectVariant,
                          index,
                          e.target.value,
                          region,
                          externalProjectType
                        );
                    }}
                  />

                  {/* Popup Heading */}
                  <p className="font-bold mt-5">Popup Heading</p>
                  <div className="flex flex-row gap-1 my-2">
                    {["en", "de", "fr", "it"].map((lang) => (
                      <Input
                        key={lang}
                        className={"w-full"}
                        inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                        labelClassName={"text-sm font-normal text-black"}
                        label={lang.toUpperCase() + " Heading"}
                        placeholder={lang}
                        value={studentRandomInputsPopupHeading?.[lang] || ""}
                        onChange={(e) => {
                          if (updateStudentRandomInputsField)
                            updateStudentRandomInputsField(
                              type,
                              projectVariant,
                              index,
                              "popupHeading",
                              lang,
                              e.target.value,
                              region,
                              externalProjectType
                            );
                        }}
                      />
                    ))}
                  </div>

                  {/* Popup Submit Button Text */}
                  <p className="font-bold mt-5">Popup Submit Button Text</p>
                  <div className="flex flex-row gap-1 my-2">
                    {["en", "de", "fr", "it"].map((lang) => (
                      <Input
                        key={lang}
                        className={"w-full"}
                        inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                        labelClassName={"text-sm font-normal text-black"}
                        label={lang.toUpperCase() + " Button"}
                        placeholder={lang}
                        value={studentRandomInputsPopupSubmitButtonText?.[lang] || ""}
                        onChange={(e) => {
                          if (updateStudentRandomInputsField)
                            updateStudentRandomInputsField(
                              type,
                              projectVariant,
                              index,
                              "popupSubmitButtonText",
                              lang,
                              e.target.value,
                              region,
                              externalProjectType
                            );
                        }}
                      />
                    ))}
                  </div>

                  {/* Popup Input Placeholder */}
                  <p className="font-bold mt-5">Popup Input Placeholder</p>
                  <div className="flex flex-row gap-1 my-2">
                    {["en", "de", "fr", "it"].map((lang) => (
                      <Input
                        key={lang}
                        className={"w-full"}
                        inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                        labelClassName={"text-sm font-normal text-black"}
                        label={lang.toUpperCase() + " Placeholder"}
                        placeholder={lang}
                        value={studentRandomInputsPopupInputPlaceholder?.[lang] || ""}
                        onChange={(e) => {
                          if (updateStudentRandomInputsField)
                            updateStudentRandomInputsField(
                              type,
                              projectVariant,
                              index,
                              "popupInputPlaceholder",
                              lang,
                              e.target.value,
                              region,
                              externalProjectType
                            );
                        }}
                      />
                    ))}
                  </div>

                  <p className="font-bold mt-5">Student Random Inputs Word Cloud ID</p>
                  <Input
                    className={"w-full"}
                    inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                    value={studentRandomInputsWordCloudId}
                    onChange={(e) => {
                      if (updateStudentRandomInputsField)
                        updateStudentRandomInputsField(
                          type,
                          projectVariant,
                          index,
                          "studentRandomInputsWordCloudId",
                          "",
                          e.target.value,
                          region,
                          externalProjectType
                        );
                    }}
                  />

                  {/* Student Random Inputs Options */}
                  <p className="font-bold mt-5">Input Options (Dropdown)</p>
                  <div className="flex flex-col gap-1 my-2">
                    {studentRandomInputsOptions?.map((option, optionIndex) => (
                      <div
                        key={`studentRandomInputsOption-${index}-${optionIndex}`}
                        className="flex flex-row items-center justify-center gap-1 my-2">
                        {["en", "de", "fr", "it"].map((lang) => (
                          <Input
                            key={`option-${optionIndex}-${lang}`}
                            className={"w-full"}
                            inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                            labelClassName={"text-sm font-normal text-black"}
                            label={lang.toUpperCase()}
                            placeholder={lang}
                            value={option?.[lang] || ""}
                            onChange={(e) => {
                              if (updateStudentRandomInputsOption)
                                updateStudentRandomInputsOption(
                                  type,
                                  projectVariant,
                                  index,
                                  optionIndex,
                                  lang,
                                  e.target.value,
                                  region,
                                  externalProjectType
                                );
                            }}
                          />
                        ))}
                        <button
                          className="text-red-600 p-2"
                          onClick={() => {
                            if (removeStudentRandomInputsOption)
                              removeStudentRandomInputsOption(
                                type,
                                projectVariant,
                                index,
                                optionIndex,
                                region,
                                externalProjectType
                              );
                          }}>
                          <TrashIcon className="w-7 h-7 mt-5" />
                        </button>
                      </div>
                    ))}
                    <button
                      className="bg-green-600 text-white rounded-md p-2 flex gap-2 items-center justify-center"
                      onClick={() =>
                        addStudentRandomInputsOption(
                          type,
                          projectVariant,
                          index,
                          region,
                          externalProjectType
                        )
                      }>
                      <PlusCircleIcon className="w-7 h-7" /> Add New Option
                    </button>
                  </div>
                </>
              )}

              {componentType === "studentRandomInputsWordCloud" && (
                <>
                  <p className="font-bold mt-5">Student Random Inputs Word Cloud ID</p>
                  <Input
                    className={"w-full"}
                    inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                    value={studentRandomInputsWordCloudId}
                    onChange={(e) => {
                      if (updateStudentRandomInputsField)
                        updateStudentRandomInputsField(
                          type,
                          projectVariant,
                          index,
                          "studentRandomInputsWordCloudId",
                          "",
                          e.target.value,
                          region,
                          externalProjectType
                        );
                    }}
                  />
                </>
              )}

              {componentType === "profileVideoSelection" && (
                <>
                  <p className="font-bold mt-5">Custom Video Selection Options</p>

                  <div className="flex flex-col gap-1 my-2">
                    {customVideoOptions?.map((option, customVideoOptionIndex) => {
                      return (
                        <div
                          key={`customVideoTitle-options-${index}-${customVideoOptionIndex}`}
                          className="flex flex-row items-center justify-center gap-1 my-2">
                          {["en", "de", "fr", "it"].map((lang) => {
                            return (
                              <Input
                                className={"w-full"}
                                inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                                labelClassName={"text-sm font-normal text-black"}
                                label={lang.toUpperCase() + " Title"}
                                placeholder={lang}
                                value={option?.[lang]}
                                onChange={(e) => {
                                  if (updateCustomVideoOption)
                                    updateCustomVideoOption(
                                      type,
                                      projectVariant,
                                      index,
                                      customVideoOptionIndex,
                                      lang,
                                      e.target.value,
                                      region,
                                      externalProjectType
                                    );
                                }}
                              />
                            );
                          })}

                          <button
                            className="text-red-600 p-2"
                            onClick={() => {
                              if (deleteCustomVideoOption)
                                deleteCustomVideoOption(
                                  type,
                                  projectVariant,
                                  index,
                                  customVideoOptionIndex,
                                  region,
                                  externalProjectType
                                );
                            }}>
                            <TrashIcon className="w-7 h-7 mt-5" />
                          </button>
                        </div>
                      );
                    })}
                    <button
                      className="bg-green-600 text-white rounded-md p-2 flex gap-2 items-center justify-center"
                      onClick={() =>
                        addCustomVideoOption(
                          type,
                          projectVariant,
                          index,
                          region,
                          externalProjectType
                        )
                      }>
                      <PlusCircleIcon className="w-7 h-7" /> Add New Option
                    </button>
                  </div>
                </>
              )}

              {componentType === "dataTable" && (
                <>
                  <p className="font-bold mt-5">Sub Input Field</p>

                  <div className="flex flex-col gap-1 my-2">
                    {["en", "de", "fr", "it"].map((lang) => (
                      <Input
                        key={lang}
                        className={"w-full"}
                        inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                        labelClassName={"text-sm font-normal text-black"}
                        label={lang.toUpperCase() + " Title"}
                        placeholder={lang}
                        value={subInputField?.[lang] || ""}
                        onChange={(e) => {
                          if (updateSubInputField)
                            updateSubInputField(
                              type,
                              projectVariant,
                              index,
                              lang,
                              e.target.value,
                              region,
                              externalProjectType
                            );
                        }}
                      />
                    ))}
                  </div>

                  <p className="font-bold mt-5">Table Columns</p>

                  <div className="flex flex-col gap-1 my-2">
                    {columns?.map((column, columnIndex) => {
                      return (
                        <div
                          key={`column-${index}-${columnIndex}`}
                          className="flex flex-row items-center justify-center gap-1 my-2">
                          {["en", "de", "fr", "it"].map((lang) => {
                            return (
                              <Input
                                key={lang}
                                className={"w-full"}
                                inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                                labelClassName={"text-sm font-normal text-black"}
                                label={lang.toUpperCase() + " Title"}
                                placeholder={lang}
                                value={column?.[lang]}
                                onChange={(e) => {
                                  if (updateTableColumn)
                                    updateTableColumn(
                                      type,
                                      projectVariant,
                                      index,
                                      columnIndex,
                                      lang,
                                      e.target.value,
                                      region,
                                      externalProjectType
                                    );
                                }}
                              />
                            );
                          })}

                          <button
                            className="text-red-600 p-2"
                            onClick={() => {
                              if (deleteTableColumn)
                                deleteTableColumn(
                                  type,
                                  projectVariant,
                                  index,
                                  columnIndex,
                                  region,
                                  externalProjectType
                                );
                            }}>
                            <TrashIcon className="w-7 h-7 mt-5" />
                          </button>
                        </div>
                      );
                    })}
                    <button
                      className="bg-green-600 text-white rounded-md p-2 flex gap-2 items-center justify-center"
                      onClick={() =>
                        addTableColumn(type, projectVariant, index, region, externalProjectType)
                      }>
                      <PlusCircleIcon className="w-7 h-7" /> Add New Column
                    </button>
                  </div>
                </>
              )}

              {componentType === "attitudesComponent" && (
                <>
                  {/* Title */}
                  <p className="font-bold mt-5">Title</p>
                  <div className="flex flex-row gap-1 my-2">
                    {["en", "de", "fr", "it"].map((lang) => (
                      <Input
                        key={lang}
                        className={"w-full"}
                        inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                        labelClassName={"text-sm font-normal text-black"}
                        label={lang.toUpperCase() + " Title"}
                        placeholder={lang}
                        value={attitudesTitle?.[lang] || ""}
                        onChange={(e) => {
                          if (updateAttitudeComponentField)
                            updateAttitudeComponentField(
                              type,
                              projectVariant,
                              index,
                              "title",
                              lang,
                              e.target.value,
                              region,
                              externalProjectType
                            );
                        }}
                      />
                    ))}
                  </div>

                  {/* Subtitle */}
                  <p className="font-bold mt-5">Subtitle</p>
                  <div className="flex flex-row gap-1 my-2">
                    {["en", "de", "fr", "it"].map((lang) => (
                      <Input
                        key={lang}
                        className={"w-full"}
                        inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                        labelClassName={"text-sm font-normal text-black"}
                        label={lang.toUpperCase() + " Subtitle"}
                        placeholder={lang}
                        value={attitudesSubtitle?.[lang] || ""}
                        onChange={(e) => {
                          if (updateAttitudeComponentField)
                            updateAttitudeComponentField(
                              type,
                              projectVariant,
                              index,
                              "subtitle",
                              lang,
                              e.target.value,
                              region,
                              externalProjectType
                            );
                        }}
                      />
                    ))}
                  </div>

                  {/* Title Number 2 */}
                  <p className="font-bold mt-5">Title Number 2</p>
                  <div className="flex flex-row gap-1 my-2">
                    {["en", "de", "fr", "it"].map((lang) => (
                      <Input
                        key={lang}
                        className={"w-full"}
                        inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                        labelClassName={"text-sm font-normal text-black"}
                        label={lang.toUpperCase() + " Title 2"}
                        placeholder={lang}
                        value={attitudesTitleNumber2?.[lang] || ""}
                        onChange={(e) => {
                          if (updateAttitudeComponentField)
                            updateAttitudeComponentField(
                              type,
                              projectVariant,
                              index,
                              "titleNumber2",
                              lang,
                              e.target.value,
                              region,
                              externalProjectType
                            );
                        }}
                      />
                    ))}
                  </div>

                  {/* Subtitle Number 2 */}
                  <p className="font-bold mt-5">Subtitle Number 2</p>
                  <div className="flex flex-row gap-1 my-2">
                    {["en", "de", "fr", "it"].map((lang) => (
                      <Input
                        key={lang}
                        className={"w-full"}
                        inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                        labelClassName={"text-sm font-normal text-black"}
                        label={lang.toUpperCase() + " Subtitle 2"}
                        placeholder={lang}
                        value={attitudesSubtitleNumber2?.[lang] || ""}
                        onChange={(e) => {
                          if (updateAttitudeComponentField)
                            updateAttitudeComponentField(
                              type,
                              projectVariant,
                              index,
                              "subtitleNumber2",
                              lang,
                              e.target.value,
                              region,
                              externalProjectType
                            );
                        }}
                      />
                    ))}
                  </div>

                  {/* Attitudes List */}
                  <p className="font-bold mt-5">Attitudes List</p>
                  <div className="flex flex-col gap-1 my-2">
                    {attitudesList?.map((attitude, attitudeIndex) => (
                      <div
                        key={`attitude-${index}-${attitudeIndex}`}
                        className="border border-gray-300 p-3 rounded-md mb-3">
                        <div className="flex justify-between items-center mb-2">
                          <p className="font-semibold">Attitude {attitudeIndex + 1}</p>
                          <button
                            className="text-red-600 p-1"
                            onClick={() => {
                              if (deleteAttitudeListItem)
                                deleteAttitudeListItem(
                                  type,
                                  projectVariant,
                                  index,
                                  attitudeIndex,
                                  region,
                                  externalProjectType
                                );
                            }}>
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>

                        {/* Attitude Title */}
                        <p className="font-medium mb-1">Title</p>
                        <div className="flex flex-row gap-1 mb-3">
                          {["en", "de", "fr", "it"].map((lang) => (
                            <Input
                              key={`title-${lang}`}
                              className={"w-full"}
                              inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                              labelClassName={"text-xs font-normal text-gray-600"}
                              label={lang.toUpperCase()}
                              placeholder={lang}
                              value={attitude?.title?.[lang] || ""}
                              onChange={(e) => {
                                if (updateAttitudeListItem)
                                  updateAttitudeListItem(
                                    type,
                                    projectVariant,
                                    index,
                                    attitudeIndex,
                                    "title",
                                    lang,
                                    e.target.value,
                                    region,
                                    externalProjectType
                                  );
                              }}
                            />
                          ))}
                        </div>

                        {/* Attitude Description */}
                        <p className="font-medium mb-1">Description</p>
                        <div className="flex flex-row gap-1">
                          {["en", "de", "fr", "it"].map((lang) => (
                            <TextArea
                              key={`desc-${lang}`}
                              className={"w-full"}
                              inputClassName={"border-none bg-[#F6F5F5] rounded-md"}
                              labelClassName={"text-xs font-normal text-gray-600"}
                              label={lang.toUpperCase()}
                              placeholder={lang}
                              value={attitude?.description?.[lang] || ""}
                              onChange={(e) => {
                                if (updateAttitudeListItem)
                                  updateAttitudeListItem(
                                    type,
                                    projectVariant,
                                    index,
                                    attitudeIndex,
                                    "description",
                                    lang,
                                    e.target.value,
                                    region,
                                    externalProjectType
                                  );
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    ))}

                    <button
                      className="bg-green-600 text-white rounded-md p-2 flex gap-2 items-center justify-center"
                      onClick={() =>
                        addAttitudeListItem(
                          type,
                          projectVariant,
                          index,
                          region,
                          externalProjectType
                        )
                      }>
                      <PlusCircleIcon className="w-7 h-7" /> Add New Attitude
                    </button>
                  </div>

                  {/* Submit Button Text */}
                  <p className="font-bold mt-5">Submit Button Text</p>
                  <div className="flex flex-row gap-1 my-2">
                    {["en", "de", "fr", "it"].map((lang) => (
                      <Input
                        key={lang}
                        className={"w-full"}
                        inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                        labelClassName={"text-sm font-normal text-black"}
                        label={lang.toUpperCase() + " Button"}
                        placeholder={lang}
                        value={attitudesSubmitButtonText?.[lang] || ""}
                        onChange={(e) => {
                          if (updateAttitudeComponentField)
                            updateAttitudeComponentField(
                              type,
                              projectVariant,
                              index,
                              "submitButtonText",
                              lang,
                              e.target.value,
                              region,
                              externalProjectType
                            );
                        }}
                      />
                    ))}
                  </div>
                </>
              )}

              {componentType === "classMapSelections" && (
                <>
                  {/* Map Images */}
                  <p className="font-bold mt-5">Map Images</p>
                  <div className="grid grid-cols-1 gap-4 mt-3">
                    {["sf", "st", "nf", "nt"].map((mapPosition) => {
                      return (
                        <div>
                          <p>{mapPosition?.toUpperCase()} - Icons Image</p>
                          {/* Single image uploader */}
                          <ImageUploader
                            image={mapImages?.icons?.[mapPosition]}
                            setImage={(image) => {
                              if (updateMapImage)
                                updateMapImage(
                                  type,
                                  projectVariant,
                                  index,
                                  `${mapPosition}_icons_img`,
                                  null,
                                  image,
                                  region,
                                  externalProjectType
                                );
                            }}
                            uploadProgress={imageUploading?.[`${mapPosition}_icons_img`] || 0}
                          />
                          {mapImages?.icons?.[mapPosition]?.type &&
                            !imageUploading?.[`${mapPosition}_icons_img`] && (
                              <button
                                onClick={async () => {
                                  const imageUrl = await handleImageUpload(
                                    `${mapPosition}_icons_img`,
                                    mapImages?.icons?.[mapPosition]
                                  );

                                  if (updateMapImage) {
                                    updateMapImage(
                                      type,
                                      projectVariant,
                                      index,
                                      `${mapPosition}_icons_img`,
                                      null,
                                      imageUrl,
                                      region,
                                      externalProjectType
                                    );
                                  }
                                }}
                                className="text-lg bg-corange-100 text-white p-2 rounded-md mt-2">
                                Confirm Upload this image
                              </button>
                            )}

                          <p className="font-semibold mb-2">{mapPosition?.toUpperCase()} Images</p>
                          <div className="grid grid-cols-2 gap-2">
                            {["en", "de", "fr", "it"].map((lang) => (
                              <div key={`${mapPosition}-${lang}`}>
                                <p className="text-sm font-medium text-gray-600 mb-1">
                                  {lang.toUpperCase()} - {mapPosition?.toUpperCase()} Image
                                </p>
                                <ImageUploader
                                  image={mapImages?.[mapPosition]?.[lang]}
                                  setImage={(image) => {
                                    if (updateMapImage)
                                      updateMapImage(
                                        type,
                                        projectVariant,
                                        index,
                                        `${mapPosition}_img`,
                                        lang,
                                        image,
                                        region,
                                        externalProjectType
                                      );
                                  }}
                                  label={`${lang.toUpperCase()} - ${mapPosition?.toUpperCase()} Image`}
                                  uploadProgress={
                                    imageUploading?.[`${mapPosition}_img.${lang}`] || 0
                                  }
                                />
                                {mapImages?.[mapPosition]?.[lang]?.type &&
                                  !imageUploading?.[`${mapPosition}_img.${lang}`] && (
                                    <button
                                      onClick={async () => {
                                        const imageUrl = await handleImageUpload(
                                          `${mapPosition}_img.${lang}`,
                                          mapImages?.[mapPosition]?.[lang]
                                        );

                                        if (updateMapImage) {
                                          updateMapImage(
                                            type,
                                            projectVariant,
                                            index,
                                            `${mapPosition}_img`,
                                            lang,
                                            imageUrl,
                                            region,
                                            externalProjectType
                                          );
                                        }
                                      }}
                                      className="text-lg bg-corange-100 text-white p-2 rounded-md mt-2">
                                      Confirm Upload this image
                                    </button>
                                  )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}

          {componentType === "htmlBox" && updateHTMLBox !== undefined && (
            <>
              <p className="font-bold">HTML Box Content</p>
              <div className="flex flex-col gap-1 my-2">
                {["en", "de", "fr", "it"].map((lang) => {
                  return (
                    <React.Fragment key={`htmlBox-${lang}`}>
                      <TextArea
                        className={"w-full"}
                        inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                        labelClassName={"text-sm font-normal text-black"}
                        label={lang.toUpperCase() + " Content"}
                        placeholder={`<div>${lang}</div>`}
                        value={html?.[lang]}
                        onChange={(e) => {
                          if (updateHTMLBox)
                            updateHTMLBox(
                              type,
                              projectVariant,
                              index,
                              lang,
                              e.target.value,
                              region,
                              externalProjectType
                            );
                        }}
                      />

                      {html?.[lang] && (
                        <>
                          <p className="font-bold text-orange-600">
                            {lang?.toUpperCase()} Preview (This is approximation. May differ
                            slightly on site)
                          </p>
                          <div
                            className="w-full p-3 rounded-md bg-white border border-gray-400"
                            dangerouslySetInnerHTML={{ __html: html?.[lang] }}
                          />
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </>
          )}

          {componentType === "infoPopup" && updateInfoPopupField !== undefined && (
            <>
              <p className="font-bold">Popup Heading</p>
              <div className="grid grid-cols-4 gap-1 my-2">
                {["en", "de", "fr", "it"].map((lang) => (
                  <Input
                    key={`ip-heading-${lang}`}
                    className={"w-full"}
                    inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                    labelClassName={"text-sm font-normal text-black"}
                    label={lang.toUpperCase() + " Heading"}
                    placeholder={lang}
                    value={infoPopupHeading?.[lang] ?? ""}
                    onChange={(e) =>
                      updateInfoPopupField(
                        type,
                        projectVariant,
                        index,
                        "heading",
                        e.target.value,
                        region,
                        externalProjectType,
                        lang
                      )
                    }
                  />
                ))}
              </div>
              <p className="font-bold mt-3">Sub Heading</p>
              <div className="grid grid-cols-4 gap-1 my-2">
                {["en", "de", "fr", "it"].map((lang) => (
                  <Input
                    key={`ip-sub-${lang}`}
                    className={"w-full"}
                    inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                    labelClassName={"text-sm font-normal text-black"}
                    label={lang.toUpperCase() + " Sub"}
                    placeholder={lang}
                    value={infoPopupSubHeading?.[lang] ?? ""}
                    onChange={(e) =>
                      updateInfoPopupField(
                        type,
                        projectVariant,
                        index,
                        "subHeading",
                        e.target.value,
                        region,
                        externalProjectType,
                        lang
                      )
                    }
                  />
                ))}
              </div>
              {[
                ["Heading Color", "headingColor", infoPopupHeadingColor],
                ["Sub Heading Color", "subHeadingColor", infoPopupSubHeadingColor],
                ["Background Color", "backgroundColor", infoPopupBackgroundColor],
                [
                  "Close Icon Background Color",
                  "closeIconBackgroundColor",
                  infoPopupCloseIconBackgroundColor
                ],
                ["Close Icon Color", "closeIconColor", infoPopupCloseIconColor],
                [
                  "Button Background Color",
                  "buttonBackgroundColor",
                  infoPopupButtonBackgroundColor
                ],
                ["Button Text Color", "buttonTextColor", infoPopupButtonTextColor]
              ].map(([label, field, val]) => (
                <div key={field} className="w-full mt-2">
                  <p className="font-bold">{label}</p>
                  <div className="flex items-center gap-4 mt-1">
                    <ColorPicker
                      color={val || "#000000"}
                      handleChange={(c) =>
                        updateInfoPopupField(
                          type,
                          projectVariant,
                          index,
                          field,
                          convertRGBAObjectToString(c),
                          region,
                          externalProjectType
                        )
                      }
                      width="w-10"
                      height="h-10"
                    />
                    <Input
                      className={"flex-1"}
                      inputClassName={
                        "rounded-full border-none bg-gray-100 focus:ring-0 focus:outline-none"
                      }
                      placeholder="#163F9B"
                      value={val ?? ""}
                      onChange={(e) =>
                        updateInfoPopupField(
                          type,
                          projectVariant,
                          index,
                          field,
                          e.target.value,
                          region,
                          externalProjectType
                        )
                      }
                    />
                  </div>
                </div>
              ))}
              <p className="font-bold mt-3">Button Text</p>
              <div className="grid grid-cols-4 gap-1 my-2">
                {["en", "de", "fr", "it"].map((lang) => (
                  <Input
                    key={`ip-btntext-${lang}`}
                    className={"w-full"}
                    inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                    labelClassName={"text-sm font-normal text-black"}
                    label={lang.toUpperCase() + " Button"}
                    placeholder={lang}
                    value={buttonText?.[lang] ?? ""}
                    onChange={(e) =>
                      updateButtonText(
                        type,
                        projectVariant,
                        index,
                        lang,
                        e.target.value,
                        region,
                        externalProjectType
                      )
                    }
                  />
                ))}
              </div>
              <div className="flex items-center justify-between my-4">
                <p className="font-bold">Auto show popup</p>
                <ToggleComponent
                  handleCompToggle={() =>
                    updateInfoPopupField(
                      type,
                      projectVariant,
                      index,
                      "autoShowPopup",
                      !infoPopupAutoShow,
                      region,
                      externalProjectType
                    )
                  }
                  isToggle={!!infoPopupAutoShow}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export const ClassSudentComponentsPicker = ({
  type = "classPage",
  projectVariant,
  addComponent
}) => {
  const componentTypes = Object.keys(componentTitles[type]);

  return (
    <div className="p-3 rounded-md bg-gray-200 w-full relative">
      <p className="font-bold text-lg text-center mt-2 mb-4">Add Component</p>
      <InputSelect
        name="componentType"
        className={"mb-0"}
        options={componentTypes.map((componentType) => ({
          value: componentType,
          label: componentTitles[type][componentType]
        }))}
        onChange={(e) => addComponent(type, projectVariant, e.value)}
      />
    </div>
  );
};

export default ClassStudentComponents;
